import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  Settings, 
  Download, 
  AlertCircle,
  Plus,
  Trash2,
  Star
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBillingStore } from '../stores/billingStore';
import AddPaymentMethod from '../components/AddPaymentMethod';
import { formatPrice } from '../utils/formatters';

const Billing = () => {
  const { user } = useAuth();
  const { 
    subscription, 
    paymentMethods,
    invoices,
    removePaymentMethod,
    setDefaultPaymentMethod,
    cancelSubscription
  } = useBillingStore();
  
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  if (!user) return null;

  const defaultPaymentMethod = paymentMethods.find(m => m.isDefault);

  const handleCancelSubscription = () => {
    cancelSubscription();
    setShowCancelConfirm(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      case 'past_due':
        return 'bg-red-100 text-red-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
          <Link
            to="/settings"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <Settings className="h-5 w-5 mr-1" />
            <span>Settings</span>
          </Link>
        </div>

        {/* Current Plan */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Current Plan</h2>
              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Premium Subscription</span>
                {subscription && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    getStatusColor(subscription.status)
                  }`}>
                    {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                  </span>
                )}
              </div>
            </div>
            {subscription && !subscription.cancelAtPeriodEnd && (
              <button 
                onClick={() => setShowCancelConfirm(true)}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Cancel Subscription
              </button>
            )}
          </div>

          {subscription ? (
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  {defaultPaymentMethod ? (
                    <p className="font-medium">•••• {defaultPaymentMethod.lastFour}</p>
                  ) : (
                    <p className="text-sm text-red-600">No payment method</p>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Next Billing Date</p>
                  <p className="font-medium">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <Star className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Monthly Amount</p>
                  <p className="font-medium">{formatPrice(subscription.price)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-600 mb-4">You don't have an active subscription</p>
              <Link
                to="/subscribe"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Upgrade to Premium
              </Link>
            </div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {paymentMethods.map((method) => (
              <div key={method.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <CreditCard className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {method.brand} •••• {method.lastFour}
                    </p>
                    <p className="text-sm text-gray-600">
                      Expires {method.expiryMonth}/{method.expiryYear}
                    </p>
                  </div>
                  {method.isDefault && (
                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Default
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  {!method.isDefault && (
                    <>
                      <button
                        onClick={() => setDefaultPaymentMethod(method.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        Make Default
                      </button>
                      <button
                        onClick={() => removePaymentMethod(method.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            <div className="px-6 py-4">
              <button
                onClick={() => setShowAddPayment(true)}
                className="flex items-center text-indigo-600 hover:text-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Payment Method
              </button>
            </div>
          </div>
        </div>

        {/* Billing History */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Billing History</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {invoices.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                No billing history available
              </div>
            ) : (
              invoices.map((invoice) => (
                <div key={invoice.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="mr-4">
                      {invoice.status === 'paid' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : invoice.status === 'pending' ? (
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatPrice(invoice.amount)} - {new Date(invoice.date).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600">{invoice.description}</p>
                    </div>
                  </div>
                  {invoice.receiptUrl && (
                    <a
                      href={invoice.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-600 hover:text-gray-900"
                    >
                      <Download className="h-5 w-5 mr-1" />
                      <span>Download</span>
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Payment Method Modal */}
      {showAddPayment && (
        <AddPaymentMethod
          onClose={() => setShowAddPayment(false)}
          onSuccess={() => setShowAddPayment(false)}
        />
      )}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Cancel Subscription?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;